import { useEffect, useMemo, useRef, useState } from "react";

import { LANGUAGE_CONFIG } from "../../../config";
import { useUiText } from "../../../hooks/useUiText";
import { translateText } from "../../../services/api/core";
import { debounce } from "../../../utils/common/functools";

/** @typedef {{ translatedText: string, detectedSourceLanguage: string|null, error: string|null }} WorkDescriptionTranslationResult */

/** @type {Map<string, WorkDescriptionTranslationResult>} */
const translationCache = new Map();
/** @type {Map<string, Promise<WorkDescriptionTranslationResult>>} */
const translationInflight = new Map();

const EMPTY_RESULT = {
  translatedText: "",
  detectedSourceLanguage: null,
  error: null,
};

/**
 * Cache key so clock-in (`es`) and payout (`en`) translations do not collide.
 *
 * @param {string} target Target language code.
 * @param {string} sourceText Trimmed source text.
 * @returns {string} Cache key.
 */
function translationCacheKey(target, sourceText) {
  return `${target}:${sourceText}`;
}

/**
 * Translate one work description through Django (v2) with source omitted
 * so Google detects the language on the same call. Shares a page-level cache.
 *
 * @param {string} sourceText Trimmed source text.
 * @param {string} target Target language code.
 * @returns {Promise<WorkDescriptionTranslationResult>}
 */
function loadTranslation(sourceText, target) {
  const key = translationCacheKey(target, sourceText);
  if (translationCache.has(key)) {
    return Promise.resolve(translationCache.get(key));
  }
  if (translationInflight.has(key)) {
    return translationInflight.get(key);
  }

  const request = translateText(sourceText, undefined, target)
    .then((result) => {
      if (result?.success) {
        const next = {
          translatedText: result.translatedText || "",
          detectedSourceLanguage: result.detectedSourceLanguage || null,
          error: null,
        };
        translationCache.set(key, next);
        return next;
      }
      const error =
        result?.error === "ip_restricted" ? "ip_restricted" : "translate_failed";
      return { ...EMPTY_RESULT, error };
    })
    .catch((error) => {
      const errorCode = error.response?.data?.error;
      return {
        ...EMPTY_RESULT,
        error: errorCode === "ip_restricted" ? "ip_restricted" : "translate_failed",
      };
    })
    .finally(() => {
      translationInflight.delete(key);
    });

  translationInflight.set(key, request);
  return request;
}

/**
 * Spanish-only work-description translation. Never writes back into saved text.
 *
 * @param {string} [text=""] Source work description.
 * @param {Object} [options] Hook options.
 * @param {number} [options.debounceMs=0] Delay before calling translate.
 * @param {string} [options.target="es"] Translation target language.
 * @returns {WorkDescriptionTranslationResult} Translation result.
 */
export function useWorkDescriptionTranslation(text = "", options = {}) {
  const debounceMs = options.debounceMs ?? 0;
  const target = options.target || LANGUAGE_CONFIG.SPANISH;
  const { language } = useUiText();
  const trimmedText = (text || "").trim();
  const [result, setResult] = useState(EMPTY_RESULT);
  const requestIdRef = useRef(0);

  /**
   * Run translate after the configured delay.
   *
   * @param {string} sourceText Trimmed source text.
   * @returns {void}
   */
  const requestTranslation = useMemo(() => {
    const run = (sourceText) => {
      const requestId = ++requestIdRef.current;
      loadTranslation(sourceText, target).then((next) => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setResult(next);
      });
    };
    return debounceMs > 0 ? debounce(run, debounceMs) : run;
  }, [debounceMs, target]);

  useEffect(() => {
    if (language !== LANGUAGE_CONFIG.SPANISH || !trimmedText) {
      requestIdRef.current += 1;
      setResult(EMPTY_RESULT);
      return;
    }
    const key = translationCacheKey(target, trimmedText);
    if (translationCache.has(key)) {
      setResult(translationCache.get(key));
      return;
    }
    requestTranslation(trimmedText);
  }, [language, trimmedText, target, requestTranslation]);

  return result;
}
